import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Grid,
    TextField,
    Paper,
    IconButton,
    Tooltip,
    ListItem,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles, createStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import { GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { GetLetterHeadAction, UpdateLetterHeadAction } from "../../../../../store/Ancillary/LetterHead/Action";
import { convertBase64, getDomain, getUserData, s3Client } from "../../../Base/helper/baseFunctions";

const useStyles = makeStyles((theme) =>
    createStyles({
        Paper: {
            boxShadow:
                theme.palette.mode === "dark"
                    ? "rgb(0 0 0 / 20%) 0px 0px 2px 0px, rgb(0 0 0 / 12%) 0px 12px 24px -4px"
                    : "0px 3px 3px -2px #cbc3c3, 0px 3px 4px 0px rgb(242 242 242), 0px 1px 8px 0px rgb(242 242 242)",
            padding: "24px 30px",
            width: "100%",
            borderRadius: "16px",
            backgroundColor: "#f3edfd",
        },
        dropzoneContainer: {
            border: "1px dashed rgba(93, 89, 98, 0.3)",
            borderRadius: "8px",
            padding: "24px 16px",
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
                borderColor: "rgba(93, 89, 98, 0.6)",
                backgroundColor: "#fafafa",
            },
        },
        fileItem: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            border: "1px solid rgba(93, 89, 98, 0.14)",
            backgroundColor: "#ffffff",
        },
    })
);

const LetterHead = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const classes = useStyles();
    const userData = getUserData();

    const initialValue = {
        letterhead_header: "",
        letterhead_footer: "",
        letterhead_file_path: "",
        old_letterhead_file_path: "",
    };

    const [inputData, setInputData] = useState(initialValue);
    const [pdfFiles, setPdfFiles] = useState([]);
    const { letterHeadData } = useSelector((state) => state?.AncillaryReducer?.LetterHeadReducer);

    useEffect(() => {
        dispatch(GetLetterHeadAction());
    }, [dispatch]);

    useEffect(() => {
        if (letterHeadData?.[0]) {
            setInputData({
                letterhead_header: letterHeadData[0]?.letterhead_header || "",
                letterhead_footer: letterHeadData[0]?.letterhead_footer || "",
                letterhead_file_path: letterHeadData[0]?.letterhead_file_path || letterHeadData[0]?.letterhead_path || "",
                old_letterhead_file_path: "",
            });
            setPdfFiles([]);
        }
    }, [letterHeadData]);

    const handleInputChange = (event) => {
        setInputData({
            ...inputData,
            [event.target.name]: event.target.value,
        });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: false,
        maxSize: 5000000,
        accept: {
            "application/pdf": [".pdf"],
        },
        onDrop: (acceptedFiles) => {
            if (acceptedFiles?.length > 0) {
                setPdfFiles(acceptedFiles);
            }
        },
        onDropRejected: () => {
            toast.error("Please upload a valid PDF file under 5 MB.");
        },
    });

    const handleViewPdf = async () => {
        if (pdfFiles?.[0]) {
            const fileUrl = URL.createObjectURL(pdfFiles[0]);
            window.open(fileUrl, "_blank");
            return;
        }
        const filePath = inputData?.letterhead_file_path;
        if (!filePath) return;
        try {
            const bucketParams = {
                Bucket: process.env.REACT_APP_DO_BUCKET_NAME,
                Key: filePath,
            };
            const url = await getSignedUrl(s3Client, new GetObjectCommand(bucketParams), { expiresIn: 300 });
            window.open(url, "_blank");
        } catch (err) {
            toast.error("Unable to open letterhead PDF");
        }
    };

    const handleDeletePdf = (e) => {
        e?.stopPropagation?.();
        const currentPath = inputData?.letterhead_file_path;
        setPdfFiles([]);
        setInputData((prev) => ({
            ...prev,
            old_letterhead_file_path: currentPath || prev.old_letterhead_file_path,
            letterhead_file_path: "",
        }));
    };

    const handleSubmit = async () => {
        let file_b64 = "";
        let letterhead_file_path = inputData?.letterhead_file_path || "";
        let old_letterhead_file_path = inputData?.old_letterhead_file_path || "";

        if (pdfFiles?.[0]) {
            file_b64 = await convertBase64(pdfFiles[0]);
            const domain = userData?.domain || getDomain();
            letterhead_file_path = `${domain}/Settings/LetterHead/${uuidv4()}.pdf`;
            if (inputData?.letterhead_file_path) {
                old_letterhead_file_path = inputData?.letterhead_file_path;
            }
        }

        dispatch(
            UpdateLetterHeadAction({
                letterhead_header: inputData?.letterhead_header || "",
                letterhead_footer: inputData?.letterhead_footer || "",
                letterhead_file_path: letterhead_file_path,
                file_b64: file_b64,
                old_letterhead_file_path: old_letterhead_file_path,
            })
        );
    };

    const hasUploadedPdf = Boolean(pdfFiles?.[0] || inputData?.letterhead_file_path);
    const displayedFileName = pdfFiles?.[0]?.name || (inputData?.letterhead_file_path ? `${userData?.domain || "Company"}_Letterhead.pdf` : "");

    return (
        <Paper className={classes.Paper} sx={{ padding: "24px 30px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Grid container spacing={3}>
                    {/* Left Column: Text Header & Footer */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary", mb: 1.5 }}>
                            Header & Footer Text
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box>
                                <TextField
                                    label="Header"
                                    size="small"
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    name="letterhead_header"
                                    fullWidth
                                    onChange={handleInputChange}
                                    onKeyPress={(e) =>
                                        e.target.value?.length >= 100 && e.preventDefault()
                                    }
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        const pastedText = e.clipboardData.getData("text");
                                        const currentValue = e.target.value;
                                        if (currentValue.length + pastedText.length >= 100) {
                                            const remainingChars = 100 - currentValue.length;
                                            e.target.value = currentValue + pastedText.slice(0, remainingChars);
                                        } else {
                                            e.target.value = currentValue + pastedText;
                                        }
                                        handleInputChange(e);
                                    }}
                                    value={inputData?.letterhead_header || ""}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        textAlign: "right",
                                        color: "#888",
                                        mt: 0.5,
                                    }}
                                >
                                    {(inputData?.letterhead_header?.length || 0)}/100
                                </Typography>
                            </Box>

                            <Box>
                                <TextField
                                    label="Footer"
                                    size="small"
                                    multiline
                                    minRows={3}
                                    maxRows={5}
                                    variant="outlined"
                                    name="letterhead_footer"
                                    fullWidth
                                    onChange={handleInputChange}
                                    onKeyPress={(e) =>
                                        e.target.value?.length >= 350 && e.preventDefault()
                                    }
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        const pastedText = e.clipboardData.getData("text");
                                        const currentValue = e.target.value;
                                        if (currentValue.length + pastedText.length >= 350) {
                                            const remainingChars = 350 - currentValue.length;
                                            e.target.value = currentValue + pastedText.slice(0, remainingChars);
                                        } else {
                                            e.target.value = currentValue + pastedText;
                                        }
                                        handleInputChange(e);
                                    }}
                                    value={inputData?.letterhead_footer || ""}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        textAlign: "right",
                                        color: "#888",
                                        mt: 0.5,
                                    }}
                                >
                                    {(inputData?.letterhead_footer?.length || 0)}/350
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Column: PDF Upload / View / Delete (Candidate Resume FileUploader Style) */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary", mb: 1.5 }}>
                            Letterhead Template (PDF)
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {hasUploadedPdf ? (
                                <ListItem sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderRadius: "6px",
                                    padding: "0.625rem 0.6rem 0.625rem 1.5rem",
                                    border: "1px solid rgba(93, 89, 98, 0.14)",
                                    marginTop: 1,
                                }}>
                                    <div
                                        className="file-details"
                                        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                                        onClick={handleViewPdf}
                                    >
                                        <div style={{ marginRight: "10px", display: "flex", alignItems: "center" }} className="file-preview">
                                            <DescriptionIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />
                                        </div>
                                        <div>
                                            <Typography
                                                className="file-name"
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    color: "text.primary",
                                                }}
                                            >
                                                {displayedFileName}
                                            </Typography>
                                        </div>
                                    </div>

                                    <IconButton onClick={handleDeletePdf} size="small">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </ListItem>
                            ) : (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        cursor: "pointer",
                                        position: "relative",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: "1px dashed rgba(93, 89, 98, 0.3)",
                                        borderRadius: "8px",
                                        padding: "36px 16px",
                                        minHeight: "140px",
                                        transition: "all 0.2s ease-in-out",
                                    }}
                                    {...getRootProps({ className: "dropzone" })}
                                >
                                    <input {...getInputProps()} />
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <div className="upload_doc">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                                aria-hidden="true"
                                                role="img"
                                                className="MuiBox-root css-1ngw63v iconify iconify--eva"
                                                width="28px"
                                                height="28px"
                                                viewBox="0 0 24 24"
                                            >
                                                <g fill="currentColor">
                                                    <path d="M21.9 12c0-.11-.06-.22-.09-.33a4.17 4.17 0 0 0-.18-.57c-.05-.12-.12-.24-.18-.37s-.15-.3-.24-.44S21 10.08 21 10s-.2-.25-.31-.37s-.21-.2-.32-.3L20 9l-.36-.24a3.68 3.68 0 0 0-.44-.23l-.39-.18a4.13 4.13 0 0 0-.5-.15a3 3 0 0 0-.41-.09L17.67 8A6 6 0 0 0 6.33 8l-.18.05a3 3 0 0 0-.41.09a4.13 4.13 0 0 0-.5.15l-.39.18a3.68 3.68 0 0 0-.44.23l-.36.3l-.37.31c-.11.1-.22.19-.32.3s-.21.25-.31.37s-.18.23-.26.36s-.16.29-.24.44s-.13.25-.18.37a4.17 4.17 0 0 0-.18.57c0 .11-.07.22-.09.33A5.23 5.23 0 0 0 2 13a5.5 5.5 0 0 0 .09.91c0 .1.05.19.07.29a5.58 5.58 0 0 0 .18.58l.12.29a5 5 0 0 0 .3.56l.14.22a.56.56 0 0 0 .05.08L3 16a5 5 0 0 0 4 2h3v-1.37a2 2 0 0 1-1 .27a2.05 2.05 0 0 1-1.44-.61a2 2 0 0 1 .05-2.83l3-2.9A2 2 0 0 1 12 10a2 2 0 0 1 1.41.59l3 3a2 2 0 0 1 0 2.82A2 2 0 0 1 15 17a1.92 1.92 0 0 1-1-.27V18h3a5 5 0 0 0 4-2l.05-.05a.56.56 0 0 0 .05-.08l.14-.22a5 5 0 0 0 .3-.56l.12-.29a5.58 5.58 0 0 0 .18-.58c0-.1.05-.19.07-.29A5.5 5.5 0 0 0 22 13a5.23 5.23 0 0 0-.1-1Z" />
                                                    <path d="M12.71 11.29a1 1 0 0 0-1.4 0l-3 2.9a1 1 0 1 0 1.38 1.44L11 14.36V20a1 1 0 0 0 2 0v-5.59l1.29 1.3a1 1 0 0 0 1.42 0a1 1 0 0 0 0-1.42Z" />
                                                </g>
                                            </svg>
                                            <p className="MuiTypography-root MuiTypography-body2" style={{ margin: "4px 0 0 0", color: "rgb(145, 158, 171)" }}>
                                                Upload file
                                            </p>
                                        </div>
                                    </Box>
                                </div>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {/* Bottom Bar: Instructions & Submit Button */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        pt: 1.5,
                        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                        flexWrap: "wrap",
                        gap: 1.5,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            padding: "8px 12px",
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            borderRadius: "6px",
                            color: "text.secondary",
                        }}
                    >
                        Header and Footer are used in Payslip, Invoice, and Offer Letter. Upload professional images for consistent branding.
                    </Typography>
                    <Button
                        type="button"
                        className="mpl-primary-btn"
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{ minWidth: "100px" }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default LetterHead;
